// The browser side of src/markdown.rs: the same markdown dialect for the compose editor and the
// planner's journal. CommonMark with GFM tables, strikethrough and bare links; a newline is a line
// break; raw HTML is text. `toHtml` feeds the editor, `fromHtml` writes its document back as
// markdown, `markdownView` renders it to read.

import { Marked } from 'marked'
import TurndownService from 'turndown'
import { escapeHtml } from './format.js'

const marked = new Marked({ gfm: true, breaks: true })
marked.use({ renderer: { html: ({ text }) => escapeHtml(text) } })

const turndown = new TurndownService({
  headingStyle: 'atx',
  hr: '---',
  bulletListMarker: '*',
  codeBlockStyle: 'fenced',
  emDelimiter: '_',
  strongDelimiter: '**',
  // A line break is a plain newline: the dialect reads it as a break.
  br: '',
})

// An underscore inside a word is never emphasis, so `file_name` needs no backslash.
const escape = turndown.escape.bind(turndown)
turndown.escape = (text) => escape(text).replace(/(?<=[\p{L}\p{N}])\\_(?=[\p{L}\p{N}])/gu, '_')

turndown.addRule('strikethrough', {
  filter: ['del', 's', 'strike'],
  replacement: (content) => `~~${content}~~`,
})

// The editor wraps list item text in a paragraph; the item itself is enough.
turndown.addRule('listItemParagraph', {
  filter: (node) => node.nodeName === 'P' && node.parentNode?.nodeName === 'LI',
  replacement: (content) => content,
})

// `* item` and `1. item` with one space, not turndown's padded markers; continuation lines line up.
turndown.addRule('listItem', {
  filter: 'li',
  replacement(content, node, options) {
    const parent = node.parentNode
    let prefix = `${options.bulletListMarker} `
    if (parent.nodeName === 'OL') {
      const start = Number(parent.getAttribute('start') || 1)
      prefix = `${start + Array.prototype.indexOf.call(parent.children, node)}. `
    }
    const body = content.replace(/^\n+/, '').replace(/\n+$/, '\n').replace(/\n(?!$)/g, `\n${' '.repeat(prefix.length)}`)
    return prefix + body + (node.nextSibling && !body.endsWith('\n') ? '\n' : '')
  },
})

// A link that shows its own address stays a bare URL, unescaped, and an email address keeps the
// `<ana@x.test>` it is usually written with.
turndown.addRule('autolink', {
  filter: (node) => {
    if (node.nodeName !== 'A') return false
    const href = node.getAttribute('href') || ''
    const text = node.textContent
    return href === text || href === `mailto:${text}`
  },
  replacement: (content, node) => (node.getAttribute('href').startsWith('mailto:') ? `<${node.textContent}>` : node.textContent),
})

/** The markdown as HTML, for the editor. */
export function toHtml(md) {
  return marked.parse(md || '')
}

/** HTML from the editor (or pasted) as markdown, trimmed, at most one blank line in a row. */
export function fromHtml(html) {
  return turndown
    .turndown(html || '')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

/** The markdown as the editor will write it back, to compare pieces of it with its value. */
export function normalize(md) {
  return fromHtml(toHtml(md))
}

// Only web and mail links (and web images) render; anything else (`javascript:`, `data:`, a bare
// path) shows as its text.
const SAFE_URL = /^(https?:|mailto:)/i
const viewer = new Marked({ gfm: true, breaks: true })
viewer.use({
  renderer: { html: ({ text }) => escapeHtml(text) },
  walkTokens(token) {
    if ((token.type === 'link' || token.type === 'image') && !SAFE_URL.test(String(token.href || '').trim())) token.type = 'text'
  },
  hooks: { postprocess: (html) => html.replaceAll('<a href=', '<a target="_blank" rel="noopener noreferrer" href=') },
})

/** The markdown as HTML to show (a journal): raw HTML as text, unsafe links as text, links in a new tab. */
export function markdownView(md) {
  return viewer.parse(md || '')
}
